import GsheetsUploaded from '@wiwdata/gsheets-uploaded';
import moment from 'moment';
import constants from '@/services/constants';
import configs, { getTransactionsDashboardUri } from '@/services/configs';
import tabularTransactions from '@/services/tabular/transactionsPanel';

/**
 * Returns the products the account type contains in an html format. Products
 * are determined by bitmap where 1 is scheduling, 2 is attendance, and 4 is
 * hire.
 */
function getProducts(accountType) {
  return configs.PRODUCT_LINES
    .filter((product) => accountType & product.bitId)
    .reduce((div, transactionProduct) => {
      const span = document.createElement('SPAN');
      const spanText = document.createTextNode(transactionProduct.abbreviation);
      span.appendChild(spanText);
      span.style = transactionProduct.style;
      div.appendChild(span);
      return div;
    }, document.createElement('DIV'));
}

/**
 * Returns the products the account type contains. Products are determined by
 * bitmap where 1 is scheduling, 2 is attendance, and 4 is hire.
 */
function getProductsText(accountType) {
  return configs.PRODUCT_LINES
    .reduce((products, product) => {
      if (accountType & product.bitId) {
        products.push(product.name);
      }
      return products;
    }, [])
    .join(' | ');
}

/**
 * Returns an object containing all the account transactions information to
 * be displayed.
 *
 * @returns {object}
 */
function transactions() {
  const typeMap = constants.TRANSACTION_TYPES.reduce(
    (accumulator, entry) => {
      const nextAccumulator = { ...accumulator };
      nextAccumulator[entry.id] = [entry.text];
      return nextAccumulator;
    },
    {},
  );

  this.allTransactions = this.pulledData.rawTransactions
    .concat(this.pulledData.manualTransactions);

  const allTransactions = this.allTransactions.map((transaction) => {
    const { accountId } = this.$route.params;
    const enriched = {
      transactionId: transaction.transaction_id,
      amount: transaction.amount,
      taxAmount: transaction.tax_amount,
      unitQuantity: transaction.unit_quantity,
      planId: transaction.plan_id,
      planName: transaction.plan_name,
      productsHtml: getProducts(transaction.account_type),
      productsText: getProductsText(transaction.account_type),
      description: transaction.description,
      type: transaction.type,
      source: transaction.source,
      createdAt: transaction.created_at,
      stripeLink: transaction.stripe_link,
    };
    enriched.planIdObject = { planId: enriched.planId, tooltip: enriched.planName };
    enriched.typeString = `${enriched.type}: ${typeMap[enriched.type]}`;

    enriched.htmlLink = (
      enriched.stripeLink === null
        ? null
        : `<a href=${transaction.stripe_link} target="_blank">
              <span class="material-icons md-18" style="padding-left: 15px; padding-right: 15px">
                  launch
              </span>
           </a>`
    );
    const { transactionId } = enriched;
    enriched.refundLink = (
      enriched.stripeLink === null || enriched.type === 2
        ? transactionId
        : (
          `<a href=#/accounts/${accountId}/transactions/${transactionId}/refund>`
          + `${transactionId}</a>`
        )
    );
    return enriched;
  });
  return allTransactions
    .sort((a, b) => {
      // Descending created at.
      if (moment.utc(b.createdAt) > moment.utc(a.createdAt)) return 1;
      if (moment.utc(b.createdAt) < moment.utc(a.createdAt)) return -1;

      // Descending transaction id.
      if (parseInt(b.transactionId, 10) > parseInt(a.transactionId, 10)) return 1;
      if (parseInt(b.transactionId, 10) < parseInt(a.transactionId, 10)) return -1;

      // Ascending source so manual is on top of monolith.
      if (b.source > a.source) return 1;
      if (b.source < a.source) return -1;
      return -2;
    });
}

/**
 * Clears error message variable
 */
function clearError() {
  this.errorMessage = null;
}

/**
 * Create google sheet and open the gsheets in a new tab
 */
async function createGoogleSheets() {
  this.exporting = true;
  const { client } = window.gapi;
  try {
    const sheetsUrl = await GsheetsUploaded.createSheet(
      client,
      'Finance Portal',
      tabularTransactions.toGsheets(this.transactions),
    );
    window.open(sheetsUrl, '_blank');
  } catch (e) {
    console.error(e);
    this.errorMessage = 'Google Sheets Upload Failed';
    setTimeout(clearError, 3000);
  }
  this.exporting = false;
}

/**
 * Navigate to the stripe transactions dashboard in another tab
 */
async function goToSuperset() {
  try {
    window.open(getTransactionsDashboardUri(), '_blank');
  } catch (e) {
    console.error(e);
    this.errorMessage = 'lol you thought you could see that??';
    setTimeout(clearError, 3000);
  }
}

/**
 * Returns the table settings object required for tabulator. Convert a
 * list of transactions objects into a list of lists for tabulator's
 * consumption.
 * @returns {object} Object containing tabulator data and options
 */
function tableSettings() {
  return tabularTransactions.getTableSettings(this.transactions);
}

/**
 * Returns the table settings object required for AG Grid. Convert a
 * list of transactions objects into a list of lists for AG Grid's
 * consumption.
 * @returns {object} Object containing tabulator data and options
 */
function gridSettings() {
  return tabularTransactions.getGridSettings(this.transactions);
}

/**
 * Applies AG Grid filter (filterColumns) to the table. This function runs as the user types
 * values in the search box and reduces the rows based on what matches the query.
 */
function filter() {
  const { query } = this;
  this.gridSettings.gridOptions.api.setQuickFilter(query);
}

export default {
  filter, tableSettings, gridSettings, transactions, createGoogleSheets, goToSuperset,
};
